/**
 * Authentication Service (v2)
 * Handles all auth-related API calls using the new v2 hexagonal auth architecture
 */

import apiRequest from './api';

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @param {string} userData.email - Email address
 * @param {string} userData.password - Password
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.userType - User type ('customer', 'guide', or 'admin')
 * @param {string} [userData.phone] - Phone number (optional)
 * @returns {Promise<Object>} User and tokens
 */
export async function register(userData) {
  const response = await apiRequest('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  // Store tokens (v2 returns data.accessToken and data.refreshToken)
  if (response.data) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('rememberMe', 'false');
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  return response.data;
}

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} [rememberMe=false] - Extend session to 30 days (guides) / keep at 30 days (customers)
 * @returns {Promise<Object>} User and tokens (or MFA required info)
 */
export async function login(email, password, rememberMe = false) {
  const response = await apiRequest('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  // Store tokens (if no MFA required)
  if (response.data && response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  // Return data (may include mfaRequired flag and userId for MFA flow)
  return response.data;
}

/**
 * Login with Google OAuth
 * @param {string} credential - Google ID token (JWT) from @react-oauth/google
 * @param {string} userType - User type ('customer' or 'guide') - defaults to 'customer'
 * @returns {Promise<Object>} User and tokens
 */
export async function loginWithGoogle(credential, userType = 'customer') {
  const response = await apiRequest('/api/v1/auth/oauth/google', {
    method: 'POST',
    body: JSON.stringify({ credential, userType }),
  });

  // Store tokens
  if (response.data && response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('rememberMe', 'false');
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  return response.data;
}

/**
 * Login with Facebook OAuth (SCRUM-1793)
 * @param {string} accessToken - Facebook access token from FB.login() popup
 * @param {string} userType - User type ('customer' or 'guide')
 * @returns {Promise<Object>} User and tokens
 */
export async function loginWithFacebook(accessToken, userType = 'customer') {
  const response = await apiRequest('/api/v1/auth/oauth/facebook', {
    method: 'POST',
    body: JSON.stringify({ accessToken, userType }),
  });

  if (response.data && response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('rememberMe', 'false');
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  return response.data;
}

/**
 * Exchange a short-lived auth code for tokens (SCRUM-2100).
 * Used by AuthCallbackPage after Cognito/SAML/Google OAuth redirect flow.
 * @param {string} code - Auth code from callback URL
 * @returns {Promise<Object>} User and tokens
 */
export async function exchangeAuthCode(code) {
  const response = await apiRequest('/api/v1/auth/oauth/exchange', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

  if (response.data && response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('rememberMe', 'false');
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  return response.data;
}

/**
 * Logout current user
 * @returns {Promise<void>}
 */
export async function logout() {
  const refreshToken = localStorage.getItem('refreshToken');

  // Only call API if we have a refresh token to invalidate server-side
  if (refreshToken) {
    try {
      await apiRequest('/api/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Always clear tokens locally
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('tokenIssuedAt');

  // Dispatch logout event to clear AuthContext
  window.dispatchEvent(new CustomEvent('auth:logout', {
    detail: { reason: 'user_initiated' }
  }));
}

/**
 * Refresh access token (with automatic token rotation)
 * @returns {Promise<Object>} New tokens
 */
export async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await apiRequest('/api/v1/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });

  // Store new rotated tokens (preserve rememberMe flag across rotations)
  if (response.data) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    localStorage.setItem('tokenIssuedAt', String(Date.now()));
  }

  return response.data;
}

/**
 * Get current user info
 * @returns {Promise<Object>} User data
 */
export async function getCurrentUser() {
  const response = await apiRequest('/api/v1/auth/me', {
    method: 'GET',
  });
  return response.data;
}

/**
 * Get user profile (alias for getCurrentUser)
 * @returns {Promise<Object>} User data
 */
export async function getProfile() {
  const response = await apiRequest('/api/v1/auth/profile', {
    method: 'GET',
  });
  return response.data;
}

/**
 * Update user profile
 * @param {Object} updates - Profile updates
 * @param {string} [updates.firstName] - First name
 * @param {string} [updates.lastName] - Last name
 * @param {string} [updates.phone] - Phone number
 * @returns {Promise<Object>} Updated user data
 */
export async function updateProfile(updates) {
  const response = await apiRequest('/api/v1/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.data;
}

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Success message
 */
export async function changePassword(currentPassword, newPassword) {
  const response = await apiRequest('/api/v1/auth/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  // Clear tokens after password change (forces re-login)
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  // Dispatch logout event to clear AuthContext
  window.dispatchEvent(new CustomEvent('auth:logout', {
    detail: { reason: 'password_changed' }
  }));

  return response.data;
}

/**
 * Delete user account (soft delete)
 * @param {string} password - User password for confirmation
 * @returns {Promise<Object>} Success message
 */
export async function deleteAccount(password) {
  const response = await apiRequest('/api/v1/auth/account', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  });

  // Clear tokens after account deletion
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  return response.data;
}

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean} True if user has access token
 */
export function isAuthenticated() {
  return !!localStorage.getItem('accessToken');
}

/**
 * Get stored access token
 * @returns {string|null} Access token or null
 */
export function getAccessToken() {
  return localStorage.getItem('accessToken');
}

// ============================================================================
// EMAIL VERIFICATION
// ============================================================================

/**
 * Verify email with token from email link
 * @param {string} token - Verification token
 * @returns {Promise<Object>} Verification result
 */
export async function verifyEmail(token) {
  const response = await apiRequest('/api/v1/auth/email/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return response.data;
}

/**
 * Resend verification email
 * @param {string} email - Email address
 * @returns {Promise<Object>} Success message
 */
export async function resendVerificationEmail(email) {
  const response = await apiRequest('/api/v1/auth/email/resend', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return response.data;
}

/**
 * Check email verification status
 * @returns {Promise<Object>} Verification status
 */
export async function getEmailVerificationStatus() {
  const response = await apiRequest('/api/v1/auth/email/status', {
    method: 'GET',
  });
  return response.data;
}

// ============================================================================
// PASSWORD RESET
// ============================================================================

/**
 * Request password reset email
 * @param {string} email - Email address
 * @returns {Promise<Object>} Success message (no user enumeration)
 */
export async function requestPasswordReset(email) {
  const response = await apiRequest('/api/v1/auth/password/reset-request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return response.data;
}

/**
 * Reset password with token
 * @param {string} token - Reset token from email
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Success result
 */
export async function resetPassword(token, newPassword) {
  const response = await apiRequest('/api/v1/auth/password/reset', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
  return response.data;
}

/**
 * Verify password reset token is valid (for UI feedback)
 * @param {string} token - Reset token to verify
 * @returns {Promise<Object>} Token validity result
 */
export async function verifyResetToken(token) {
  const response = await apiRequest('/api/v1/auth/password/verify-token', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return response.data;
}

/**
 * SCRUM-2260: Request a set-initial-password email (authenticated).
 * Only sends if the user's account has no password (OAuth-only signup).
 * @returns {Promise<Object>}
 */
export async function requestSetPassword() {
  const response = await apiRequest('/api/v1/account/request-set-password', {
    method: 'POST',
  });
  return response.data;
}

/**
 * SCRUM-2260: Consume a set-password token and write the user's first password.
 * @param {string} token - Single-use token from the email link
 * @param {string} newPassword
 * @returns {Promise<Object>}
 */
export async function setInitialPassword(token, newPassword) {
  const response = await apiRequest('/api/v1/account/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
  return response.data;
}

/**
 * SCRUM-2239: Get the user's lock-out risk for an optional action.
 * Returns { action, wouldLockOut, hasPassword, otherProviders, remediations }.
 * @param {string} [action] - e.g. 'unlink_google', 'unlink_facebook', 'remove_password'
 */
export async function getLockoutRisk(action) {
  const qs = action ? `?action=${encodeURIComponent(action)}` : '';
  const response = await apiRequest(`/api/v1/account/lockout-risk${qs}`, {
    method: 'GET',
  });
  return response.data;
}

/**
 * SCRUM-2240: Link an OAuth provider to the current user.
 * Provider-agnostic: google uses `credential` (ID token), facebook uses
 * `accessToken`. Pass `'google'` or `'facebook'`.
 *
 * @param {'google'|'facebook'} provider
 * @param {string} token - Google ID token OR Facebook access token
 */
export async function linkOAuthAccount(provider, token) {
  const body = provider === 'facebook'
    ? { accessToken: token }
    : { credential: token };
  const response = await apiRequest(`/api/v1/auth/oauth/${provider}/link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

/**
 * SCRUM-2262: Unlink an OAuth provider from the current user.
 * @param {string} provider
 */
export async function unlinkOAuthAccount(provider) {
  const response = await apiRequest(`/api/v1/auth/oauth/${provider}`, {
    method: 'DELETE',
  });
  return response.data;
}

// ============================================================================
// ACCOUNT DELETION (SCRUM-2264 — MVP 1.21.11.2)
// ============================================================================

/**
 * Initiate self-service account deletion. Requires re-auth via password
 * (OAuth-only users may pass an empty password).
 *
 * @param {string[]} categories - Subset of allowed categories; empty = all
 * @param {string} password - Credential for re-auth (omit/empty for OAuth-only)
 * @returns {Promise<Object>} `{ confirmationCode, gracePeriodEndsAt, status, categories }`
 */
export async function requestAccountDeletion(categories, password, mode = 'full') {
  // SCRUM-2243: `mode` is 'full' (default — account-closing lifecycle) or
  // 'granular' (per-category purge that keeps the account active).
  const response = await apiRequest('/api/v1/account/delete', {
    method: 'POST',
    body: JSON.stringify({ categories, password, mode }),
  });
  return response.data;
}

/**
 * Returns the user's current deletion request or null.
 */
export async function getDeletionStatus() {
  const response = await apiRequest('/api/v1/account/deletion-status', {
    method: 'GET',
  });
  return response.data;
}

/**
 * SCRUM-2266 — Public deletion status lookup (no auth). Used by:
 *  - The /deletion-status page that Meta reviewers visit.
 *  - The /account/reclaim page (preview before re-auth + cancel).
 * Returns only status + dates — no PII.
 * @param {string} id - deletion_request id
 * @param {string} code - confirmation_code (per-record secret)
 */
export async function getPublicDeletionStatus(id, code) {
  const qs = new URLSearchParams({ id, code }).toString();
  const response = await apiRequest(`/api/v1/account/deletion-status-public?${qs}`, {
    method: 'GET',
  });
  return response.data;
}

/**
 * Cancel (reclaim) an in-grace-period deletion request. Only valid while
 * the request is still in the 'requested' state.
 */
export async function cancelAccountDeletion() {
  const response = await apiRequest('/api/v1/account/cancel-deletion', {
    method: 'POST',
  });
  return response.data;
}

// ============================================================================
// MULTI-FACTOR AUTHENTICATION (MFA)
// ============================================================================

/**
 * Initiate MFA setup (get QR code and backup codes)
 * @returns {Promise<Object>} MFA setup data with secret, QR code, and backup codes
 */
export async function setupMFA() {
  const response = await apiRequest('/api/v1/auth/mfa/setup', {
    method: 'POST',
  });
  return response.data;
}

/**
 * Verify TOTP code to complete MFA setup
 * @param {string} code - 6-digit TOTP code from authenticator app
 * @returns {Promise<Object>} Success message
 */
export async function verifyMFASetup(code) {
  const response = await apiRequest('/api/v1/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return response.data;
}

/**
 * Disable MFA for user
 * @param {string} password - User password for confirmation
 * @returns {Promise<Object>} Success message
 */
export async function disableMFA(password) {
  const response = await apiRequest('/api/v1/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  return response.data;
}

/**
 * Verify MFA code during login
 * @param {string} userId - User ID from initial login response
 * @param {string} code - 6-digit TOTP code or backup code
 * @returns {Promise<Object>} Verification result with tokens if successful
 */
export async function verifyMFACode(userId, code) {
  const response = await apiRequest('/api/v1/auth/mfa/verify-code', {
    method: 'POST',
    body: JSON.stringify({ userId, code }),
  });

  // Store tokens if MFA verification successful
  if (response.data && response.data.accessToken) {
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
  }

  return response.data;
}

/**
 * Generate new backup codes (invalidates old ones)
 * @param {string} password - User password for confirmation
 * @returns {Promise<Object>} New backup codes array
 */
export async function regenerateBackupCodes(password) {
  const response = await apiRequest('/api/v1/auth/mfa/backup-codes', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  return response.data;
}

/**
 * Get MFA status for authenticated user
 * @returns {Promise<Object>} MFA status
 */
export async function getMFAStatus() {
  const response = await apiRequest('/api/v1/auth/mfa/status', {
    method: 'GET',
  });
  return response.data;
}
